import type { Surface, Viewport } from '@personalizehq/schemas/ir';

// The overlay chrome. One shadow-DOM host; the renderer paints into a `layer` sized to the
// viewport, and the surface decides where that layer sits and what surrounds it.
//
// THE RESET MUST LIVE INSIDE THE SHADOW ROOT. Shadow DOM blocks MATCHED RULES but NOT INHERITED
// VALUES, and `:host { all: initial }` LOSES to document rules targeting the host. A customer
// page's global `letter-spacing: 3px` would otherwise leak in and reflow every text node. This
// cost real debugging time in S0; do not "simplify" it.

export interface SurfaceHandle {
  /** What the renderer paints into. Sized vp.w x the DOCUMENT's height; all conformance rects are
   *  relative to it, and the surface anchors it inside `root`. */
  layer: HTMLElement;
  /** The SCREEN: sized to the viewport, and the box every surface anchors against. Not the window —
   *  a caller may pin a viewport smaller than it (the editor's artboard does), and a scrim or a
   *  bottom edge that tracked the window would drift away from what the device will show. */
  root: HTMLElement;
  host: HTMLElement;
  /** F15: bottom-sheet grabber chrome (null otherwise). `draw()` re-appends it into the layer after
   *  each `replaceChildren`, so it rides the sheet's top edge. */
  grabber: HTMLElement | null;
  destroy: () => void;
}

const RESET = `
  /* all:initial RESETS pointer-events back to AUTO — and .phq-root spans the whole host, so it
     becomes a full-viewport click target that swallows the customer's page. (A modal hides this:
     its scrim is a deeper auto child. A banner has no scrim, so the root itself intercepts.)
     The host's pointer-events:none must be restated AFTER the reset. Only the scrim and the
     painted document opt back in. */
  .phq-root { all: initial; display: block; pointer-events: none; }
  .phq-root * { margin: 0; padding: 0; box-sizing: border-box; }
  /* A visible scrollbar would consume cross-axis width the SOLVER DID NOT MODEL, so every
     scroll frame would be narrower in the DOM than in the golden. Suppress; scroll indicators
     become a renderer-drawn affordance if product ever wants them. */
  .phq-root *::-webkit-scrollbar { display: none; }
  .phq-root * { scrollbar-width: none; }
`;

/** Returns null for an UNKNOWN surface kind — which renders NOTHING (SPEC §11). Never a modal:
 *  guessing a surface for a document from the future is how you cover a customer's checkout. */
export function createSurface(
  kind: Surface,
  vp: Viewport,
  container: HTMLElement,
  onDismissIntent: () => void
): SurfaceHandle | null {
  // Keyed by the Surface type, so adding a surface to the IR is a TYPE ERROR here rather than a
  // silent "renders nothing" in production.
  const CHROME: Record<Surface, true> = {
    modal: true,
    fullscreen: true,
    banner: true,
    bottom_sheet: true,
    // INLINE renders inside the HOST's screen, in a slot they reserved — so on web it has no chrome
    // at all: no scrim, no dimming, nothing to dismiss. This renderer's only inline consumer is the
    // EDITOR's artboard (there is no web SDK), where it previews the widget at its slot width. The
    // real inline surface is a native view whose height the solver answers; here it is just a
    // document drawn at the top of the frame.
    inline: true,
  };
  if (!CHROME[kind]) return null;

  const host = document.createElement('div');
  host.dataset.phqHost = kind;
  host.style.position = 'fixed';
  host.style.inset = '0';
  host.style.zIndex = '2147483000';
  // A banner covers the viewport with a mostly-empty host. Without this the overlay swallows
  // EVERY click on the customer's page.
  host.style.pointerEvents = 'none';

  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = RESET;
  shadow.appendChild(style);

  const root = document.createElement('div');
  root.className = 'phq-root';
  root.style.position = 'absolute';
  root.style.left = '0';
  root.style.top = '0';
  root.style.width = `${vp.w}px`;
  root.style.height = `${vp.h}px`;
  shadow.appendChild(root);

  const layer = document.createElement('div');
  layer.dataset.phqLayer = '';
  layer.style.position = 'absolute';
  // THE LAYER IS THE DOCUMENT'S BOX, NOT THE VIEWPORT. It is viewport-WIDE (the solver centers the
  // root horizontally itself — SPEC §3 — so x is already handled and the layer must not shift it),
  // but only as TALL AS THE DOCUMENT. That is what makes the vertical anchoring below mean
  // anything: the solver fixes every root at `y = 0` and leaves vertical placement to the surface
  // chrome, so a viewport-tall layer silently pins EVERY surface to the top — a bottom sheet at the
  // top of the screen, a modal that never centers. `draw()` sets the height each frame.
  layer.style.width = `${vp.w}px`;
  layer.style.height = `${vp.h}px`;
  // THE LAYER IS NOT A CLICK TARGET. It is viewport-sized, so `auto` here buries the scrim
  // under the entire viewport (tap-to-dismiss silently dead) AND makes a banner swallow every
  // click on the customer's page, even where it paints nothing. Only the PAINTED tree opts
  // back in (render.ts sets `auto` on the document root it paints). Proven by hit-testing —
  // a programmatic .click() on the scrim element could never have caught this.
  layer.style.pointerEvents = 'none';

  let grabber: HTMLElement | null = null;
  let scrim: HTMLElement | null = null;
  // A scrim is what makes a surface MODAL in the interaction sense: it dims the app and eats the
  // taps. A banner has none, and an INLINE widget is not even over the app — it IS the app's screen.
  const wantsScrim = kind === 'modal' || kind === 'bottom_sheet';
  if (wantsScrim) {
    scrim = document.createElement('div');
    scrim.dataset.phqScrim = '';
    scrim.style.position = 'absolute';
    scrim.style.inset = '0';
    scrim.style.background = 'rgba(0,0,0,0.4)';
    scrim.style.pointerEvents = 'auto';
    scrim.addEventListener('click', (e) => {
      if (e.target === scrim) onDismissIntent();
    });
    root.appendChild(scrim);
  }

  // Vertical anchoring only — `left: 0` everywhere, because the horizontal origin is the SOLVER's
  // (`x = floor((vp.w - root.w) / 2)`). Centering the layer too would double-apply it.
  layer.style.left = '0';
  switch (kind) {
    case 'modal':
      layer.style.top = '50%';
      layer.style.transform = 'translateY(-50%)';
      break;
    case 'fullscreen':
      layer.style.top = '0';
      break;
    case 'banner':
      layer.style.top = '0';
      break;
    case 'bottom_sheet':
      layer.style.bottom = '0';
      // F15 — THE GRABBER. Without it a bottom sheet is visually a bare page pinned to the bottom,
      // and the author cannot see WHICH surface they are on — half of why switching a fullscreen step
      // to a sheet (F14) is invisible on the canvas. The iOS `Chrome.swift` draws grabber + detents +
      // rounded top; here we draw only the grabber, as pure CHROME (`pointer-events: none`) that
      // touches NO solver rect. It rides the layer's TOP edge — but `draw()` calls `replaceChildren`
      // on the layer each frame, so the handle exposes it and `draw()` re-appends it after paint.
      grabber = document.createElement('div');
      grabber.dataset.phqGrabber = '';
      grabber.style.cssText =
        'position:absolute;left:50%;top:8px;width:36px;height:5px;border-radius:3px;' +
        'transform:translateX(-50%);background:rgba(127,127,127,0.6);pointer-events:none;z-index:1;';
      break;
    case 'inline':
      // No anchoring decision to make: an inline widget occupies the slot it was given, and the slot
      // is the top of the box the editor hands us.
      layer.style.top = '0';
      break;
  }
  root.appendChild(layer);

  // Esc dismisses a modal only. ponytail: last-mounted wins if two modals coexist — acceptable
  // until a real flow needs a stack.
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') onDismissIntent();
  };
  if (kind === 'modal') document.addEventListener('keydown', onKey);

  container.appendChild(host);

  return {
    layer,
    root,
    host,
    grabber,
    destroy: () => {
      if (kind === 'modal') document.removeEventListener('keydown', onKey);
      host.remove();
    },
  };
}
